'use client'

import validator, { isNumeric } from 'validator'
import { currencyMasker, mask } from './util'
import { isCnpj, isCpf } from './common-validators'
import { useEffect, useRef, useState } from 'react'

interface FormValidationState {
    [key: string]: {
        [key: string]: boolean
    }
}

type Mixed<T = any, V = any> = {
    [P in keyof T]: V
}

const DatasetValidation = ['validators']
const DatasetActions = ['']
const DatasetConditional = [
    'equals',
    'greaterOrEqual',
    'greaterThan',
    'lessOrEqual',
    'lessThan',
    'notEquals',
]
const DatasetCurrency = [
    'currency',
    'prefix',
    'decimalSeparator',
    'groupSeparator',
    'precision',
]

interface Config {
    initialState?: Mixed
    /**
     * zero = forever
     */
    closeErrorMessageMilliseconds?: number

    /**
     * Reference
     */
    submitButton?: React.RefObject<HTMLButtonElement>

    /**
     * Called when validating a field
     */
    onValidate?: (name: string, isValid: boolean, validationType: string) => any

    invalidClasses?: string
}

type Field = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

type Item = {
    name: string
    validators: string[]
    transformers: string[]
    remove: string
    mask: string
    actions: Mixed
    field: Field
    errorElement: HTMLElement | null
    touched: boolean
    regexValidation: string
    conditional: Mixed
    currency: Mixed | null
}

export const ReactInputStateConfig: Config = {
    invalidClasses: '!input-error !textarea-error',
}

const currencyFormat = (
    value: string | number | null | undefined,
    prefix: string,
    precision: number,
    decimalSeparator: string,
    groupSeparator: string
) => {
    if (value === null || value === undefined) {
        value = '0'
    }

    value = `${value}`.replace(/[^0-9]+/g, '')

    return currencyMasker(value, {
        prefix: prefix,
        decimalSeparator,
        groupSeparator,
        precision,
    })
}

type StateEvents = 'ready' | 'init'

type StateEventAction = {
    name: string
    action: (...deps: any[]) => any
    deps: any[]
}

type StateEvent = {
    [key in StateEvents]: StateEventAction[]
}

const defaultEvents = {
    ready: [],
    init: [],
}

export class ReactFormState {
    private formState: {
        state: Mixed
        setState: React.Dispatch<React.SetStateAction<Mixed>>
    } = {
        state: {},
        setState: () => {},
    }

    private privateValues: Mixed = {}

    private events: StateEvent = defaultEvents

    public onChange: (name: string, value: any) => any = () => {}

    get values(): Mixed {
        const returnValues: Mixed = {}

        for (let name in this.formState.state) {
            if (name in this.privateValues) {
                continue
            }

            let field: Field

            if (name in this.radios) {
                const someKey = Object.keys(this.radios[name])[0]
                field = this.radios[name][someKey].field
            } else {
                field = this.fields[name]?.field
            }

            if (field && field.dataset.private) {
                continue
            }

            if (name.includes('.')) {
                const split = name.split('.')
                const parent = split[0]
                const child = split[1]

                if (!returnValues[parent]) {
                    returnValues[parent] = {}
                }

                returnValues[parent][child] = this.formState.state[name]
            } else {
                returnValues[name] = this.formState.state[name]
            }
        }

        return { ...returnValues }
    }

    private validation: FormValidationState = {}

    private fields: {
        [name: string]: Item
    } = {}

    private radios: {
        [name: string]: {
            [value: string]: Item
        }
    } = {}

    private _config: Config | undefined

    constructor(config?: Config) {
        this._config = config
        this.formState.state =
            config && config.initialState ? config.initialState : {}
    }

    private get config() {
        const config = {
            ...ReactInputStateConfig,
            ...(this._config ? this._config : {}),
        }

        return config
    }

    get btnSubmit(): HTMLElement | null {
        if (this.config.submitButton) {
            return this.config.submitButton.current
        }
        return null
    }

    private static instances: {
        [key: string]: ReactFormState
    } = {}

    static setup(id: string) {
        const state = new ReactFormState()
        this.instances[id] = state
        return state
    }

    static getInstance(id: string) {
        return this.instances[id]
    }

    setInitialState(initialState: Mixed) {
        if (!this._config) {
            this._config = {}
        }

        this._config.initialState = initialState

        for (let name in initialState) {
            this.setValue(name, initialState[name])
        }

        this.validateAll()
    }

    value(name: string, set = false, value: any = undefined) {
        if (set && !this.formState.state[name]) {
            this.formState.state[name] = value
        }
        return this.formState.state[name]
    }

    focus(name: string) {
        if (this.fields[name]) {
            this.fields[name].field?.focus()
        }
    }

    setMask(name: string, mask: string) {
        if (this.fields[name]) {
            this.fields[name].mask = mask
        }
    }

    private addField(data: {
        field: Field
        type: 'select' | 'input' | 'textarea'
    }) {
        if (!this.root) {
            return
        }

        const { field } = data

        //field.outerHTML = field.outerHTML

        if (field.dataset.ignore) {
            return
        }

        const name = field.name

        const validators: string[] = []
        const actions: Mixed = {}
        const conditional: Mixed = {}
        let currency: Mixed | null = null

        for (let name in field.dataset) {
            if (Object.hasOwn(field.dataset, name)) {
                if (DatasetValidation.includes(name)) {
                    const validatorName = field.dataset[name]
                    if (validatorName) {
                        validators.push(validatorName)
                    }
                }

                if (DatasetActions.includes(name)) {
                    actions[name] = field.dataset[name]
                }

                if (DatasetConditional.includes(name)) {
                    conditional[name] = field.dataset[name]
                }

                if (DatasetCurrency.includes(name)) {
                    if (!currency) {
                        currency = {}
                    }
                    currency[name] = field.dataset[name]
                }
            }
        }

        const errorElement = this.root.querySelector(
            '[data-error-for="' + name + '"]'
        )

        if (errorElement) {
            const element = errorElement as HTMLElement
            element.style.display = 'none'
        }

        const fieldData = {
            name,
            validators,
            field,
            actions,
            errorElement: errorElement ? (errorElement as HTMLElement) : null,
            transformers: field.dataset.transform
                ? field.dataset.transform.split(',')
                : [],
            remove: field.dataset.remove ?? '',
            mask: field.dataset.mask ?? '',
            touched: false,
            regexValidation: field.dataset.regexValidation ?? '',
            conditional,
            currency,
        }

        if (field.type != 'radio') {
            this.fields[name] = fieldData
        } else {
            if (!this.radios[name]) {
                this.radios[name] = {}
            }
            this.radios[name][`${field.value}`] = fieldData
        }

        if (this.config.initialState && name in this.config.initialState) {
            this.setValue(name, this.config.initialState[name])
        } else if (
            field instanceof HTMLInputElement ||
            field instanceof HTMLTextAreaElement ||
            field instanceof HTMLSelectElement
        ) {
            // (field.value || ('checked' in field && field.checked))

            if (field instanceof HTMLInputElement && field.type == 'checkbox') {
                this.setValue(name, field.checked)
            } else if (
                field instanceof HTMLInputElement &&
                field.type == 'radio'
            ) {
                if (field.checked) {
                    this.setValue(name, field.value)
                }
            } else if (field.value) {
                this.setValue(name, field.value)
            } else if (this.formState.state && name in this.formState.state) {
                this.setValue(name, this.formState.state[name])
            } else {
                if (name.includes('.')) {
                    const split = name.split('.')
                    const parent = split[0]
                    const child = split[1]

                    if (
                        this._config?.initialState &&
                        parent in this._config?.initialState
                    ) {
                        let object = this.value(parent, true, {})
                        object[child] =
                            this._config?.initialState[parent][child]
                        this.checkData(name)
                        this.validate(name)
                    }
                } else if (
                    this._config?.initialState &&
                    name in this._config?.initialState
                ) {
                    this.setValue(name, this._config?.initialState[name])
                }
            }
        }
    }

    private addEvents() {
        for (let fieldName in this.fields) {
            const item = this.fields[fieldName]
            this.addKeyUpEvent(item)
            this.addChangeEvent(item)
            this.addTouchEvents(item)
        }
        for (let fieldName in this.radios) {
            for (let radioValue in this.radios[fieldName]) {
                const item = this.radios[fieldName][radioValue]
                this.addKeyUpEvent(item)
                this.addChangeEvent(item)
                this.addTouchEvents(item)
            }
        }
    }

    private addTouchEvents(item: Item) {
        const field = item.field

        const handler = (event: Event) => {
            item.touched = true
        }

        field.onfocus = handler

        const handlerBlur = (event: Event) => {
            item.touched = true
            this.validate(item.name)
        }

        field.onblur = handlerBlur
    }

    private addKeyUpEvent(item: Item) {
        const submitButton = this.btnSubmit

        if (!submitButton) {
            return
        }

        const field = item.field

        const handler = (event: KeyboardEvent) => {
            if (
                event &&
                event.key &&
                event.key.toLowerCase() === 'enter' &&
                this.isValid
            ) {
                submitButton.click()
            }
        }

        field.onkeyup = handler
    }

    private addChangeEvent(item: Item) {
        const field = item.field

        if (field instanceof HTMLSelectElement) {
            const handler = (event: Event) => {
                const value = (event.target as HTMLSelectElement).value
                this.formState.state[item.name] = value
                this.validate(item.name)
                if (this.onChange) {
                    this.onChange(item.name, this.formState.state[item.name])
                }
                this.formState.setState({ ...this.formState.state })
            }

            field.oninput = handler
        } else if (field.type == 'checkbox' || field.type == 'radio') {
            const handler = (event: Event) => {
                const element = event.target as HTMLInputElement
                const value = element.value
                const checked = element.checked

                if (field.type == 'radio') {
                    this.formState.state[item.name] = value
                } else if (field.type == 'checkbox') {
                    this.formState.state[item.name] = checked
                }
                if (this.onChange) {
                    this.onChange(item.name, this.formState.state[item.name])
                }

                this.formState.setState({ ...this.formState.state })

                this.validate(item.name)
            }

            field.oninput = handler
        } else {
            const handler = (event: Event) => {
                const value = (
                    event.target as HTMLInputElement | HTMLTextAreaElement
                ).value

                this.formState.state[item.name] = value
                this.transform(item.name)
                this.remove(item.name)
                this.mask(item.name)
                this.currency(item.name)
                this.validate(item.name)

                if (this.onChange) {
                    this.onChange(item.name, this.formState.state[item.name])
                }

                this.formState.setState({ ...this.formState.state })
            }

            field.oninput = handler
        }
    }

    private currency(name: string) {
        if (!(name in this.fields)) {
            return
        }

        const { currency } = this.fields[name]

        const value = `${this.formState.state[name]}`.replace(/\D+/g, '')

        if (currency && !isNumeric(value)) {
            this.setValue(name, '', 'currency')
            return
        }

        if (currency) {
            const value = currencyFormat(
                this.formState.state[name],
                currency.prefix ?? '',
                currency.precision ? Number(currency.precision) : 2,
                currency.decimalSeparator ?? ',',
                currency.groupSeparator ?? '.'
            )

            this.setValue(name, value, 'currency')
        }
    }

    private mask(name: string) {
        if (!(name in this.fields)) {
            return
        }

        const { mask: theMask, field } = this.fields[name]

        if (theMask && typeof this.formState.state[name] == 'string') {
            const value = mask(this.formState.state[name], theMask)
            this.setValue(name, value, 'mask')
        }
    }

    private remove(name: string) {
        if (!(name in this.fields)) {
            return
        }

        const { remove, field } = this.fields[name]

        if (remove && typeof this.formState.state[name] == 'string') {
            const regex = new RegExp(remove, 'g')
            const value = this.formState.state[name].replace(regex, '')
            this.setValue(name, value, 'remove')
        }
    }

    private transform(name: string) {
        if (!(name in this.fields)) {
            return
        }

        const { transformers, field } = this.fields[name]

        if (
            (field instanceof HTMLInputElement ||
                field instanceof HTMLTextAreaElement) &&
            this.formState.state[name] &&
            typeof this.formState.state[name] == 'string'
        ) {
            for (let transformer of transformers) {
                switch (transformer) {
                    case 'lowercase':
                        const lowercase =
                            this.formState.state[name].toLowerCase()
                        this.setValue(name, lowercase, 'transform')
                        break
                    case 'uppercase':
                        const uppercase =
                            this.formState.state[name].toUpperCase()
                        this.setValue(name, uppercase, 'transform')
                        break
                    case 'titlecase':
                        let text = this.formState.state[name].split(' ')

                        let titlecase: string[] = []

                        for (let word of text) {
                            titlecase.push(
                                word.substring(0, 1).toUpperCase() +
                                    word.substring(1)
                            )
                        }

                        const titleCaseJoined = titlecase.join(' ')

                        this.setValue(name, titleCaseJoined, 'transform')
                        break
                    case 'trim':
                        const trimmed = this.formState.state[name].trim()
                        this.setValue(name, trimmed, 'transform')
                        break
                    case 'urlsafe':
                        const urlSafe = encodeURIComponent(
                            this.formState.state[name]
                        )
                        this.setValue(name, urlSafe, 'transform')
                        break
                }
            }
        }
    }

    validate(name: string) {
        if (!(name in this.fields) && !(name in this.radios)) {
            return
        }

        if (!this.validation[name]) {
            this.validation[name] = {}
        }

        // validate radio
        if (name in this.radios) {
            for (let radioValue in this.radios[name]) {
                const radioField = this.radios[name][radioValue].field

                if (
                    radioField.required &&
                    !`${this.formState.state[name] ?? ''}`.trim().length
                ) {
                    this.validation[name].required = false
                    break
                } else {
                    this.validation[name].required = true
                }
            }

            this.displayError()
            return
        }

        const { validators, field, regexValidation, conditional } =
            this.fields[name]

        if (field.required) {
            if (
                field.type != 'checkbox' &&
                !`${this.formState.state[name] ?? ''}`.trim().length
            ) {
                this.validation[name].required = false
            } else if (
                field instanceof HTMLInputElement &&
                field.type == 'checkbox' &&
                !field.checked
            ) {
                this.validation[name].required = false
            } else {
                this.validation[name].required = true
            }
        } else {
            this.validation[name].required = true
        }

        let value = this.formState.state[name] ?? ''

        if (name.includes('.')) {
            const split = name.split('.')
            const parent = split[0]
            const child = split[1]

            if (
                this.formState.state[parent] &&
                this.formState.state[parent][child]
            ) {
                value = this.formState.state[parent][child]
            }
        }

        if (validators) {
            for (let validatorName of validators) {
                switch (validatorName) {
                    case 'email':
                        if (value && !validator.isEmail(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'cpf':
                        if (value && !isCpf(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'cnpj':
                        if (value && !isCnpj(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'bic_swift':
                        if (value && !validator.isBIC(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'btc':
                        if (value && !validator.isBtcAddress(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'credit_card':
                        if (value && !validator.isCreditCard(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'ean':
                        if (value && !validator.isEAN(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'iban':
                        if (value && !validator.isIBAN(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'imei':
                        if (value && !validator.isIMEI(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'json':
                        if (value && !validator.isJSON(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'magnet_uri':
                        if (value && !validator.isMagnetURI(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'numeric':
                        if (value && !validator.isNumeric(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'octal':
                        if (value && !validator.isOctal(value)) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                    case 'strong_password':
                        if (
                            value &&
                            !validator.isStrongPassword(value, {
                                minLength: 7,
                            })
                        ) {
                            this.validation[name][validatorName] = false
                        } else {
                            this.validation[name][validatorName] = true
                        }
                        break
                }
            }
        }

        if (regexValidation) {
            const test = new RegExp(regexValidation, 'g').test(value)
            if (!test) {
                this.validation[name]['regex'] = false
            } else {
                this.validation[name]['regex'] = true
            }
        }

        if (conditional) {
            for (let conditionName in conditional) {
                const conditionValue = conditional[conditionName] ?? ''
                const conditionNumValue = Number(
                    conditional[conditionName] ?? '0'
                )
                const numValue = Number(value ?? '0')

                switch (conditionName) {
                    case 'equals':
                        if (value != conditionValue) {
                            this.validation[name]['equals'] = false
                        } else {
                            this.validation[name]['equals'] = true
                        }
                        break
                    case 'greaterOrEqual':
                        if (numValue >= conditionNumValue) {
                            this.validation[name]['greaterOrEqual'] = true
                        } else {
                            this.validation[name]['greaterOrEqual'] = false
                        }
                        break
                    case 'greaterThan':
                        if (numValue > conditionNumValue) {
                            this.validation[name]['greaterThan'] = true
                        } else {
                            this.validation[name]['greaterThan'] = false
                        }
                        break
                    case 'lessOrEqual':
                        if (numValue <= conditionNumValue) {
                            this.validation[name]['lessOrEqual'] = true
                        } else {
                            this.validation[name]['lessOrEqual'] = false
                        }
                        break
                    case 'lessThan':
                        if (numValue < conditionNumValue) {
                            this.validation[name]['lessThan'] = true
                        } else {
                            this.validation[name]['lessThan'] = false
                        }
                        break
                    case 'notEquals':
                        if (value != conditionValue) {
                            this.validation[name]['notEquals'] = true
                        } else {
                            this.validation[name]['notEquals'] = false
                        }
                        break
                }
            }
        }

        this.displayError()
    }

    private errorCloseTimeout: NodeJS.Timeout | null = null

    private displayError() {
        let disableSubmitButton = false

        const handleErrorElement = (
            errorElement: HTMLElement | null,
            touched: boolean,
            isValid: boolean
        ) => {
            if (errorElement && errorElement.style && touched && !isValid) {
                errorElement.style.display = ''

                if (this.config && this.config.closeErrorMessageMilliseconds) {
                    if (this.errorCloseTimeout) {
                        clearTimeout(this.errorCloseTimeout)
                    }

                    this.errorCloseTimeout = setTimeout(() => {
                        errorElement.style.display = 'none'
                    }, this.config.closeErrorMessageMilliseconds)
                }
            } else if (errorElement && errorElement.style) {
                errorElement.style.display = 'none'
            }
        }

        for (let name in this.validation) {
            const validationType = this.validation[name]

            if (name in this.radios) {
                const someKey = Object.keys(this.radios[name])[0]

                const { errorElement, touched, field } =
                    this.radios[name][someKey]

                const isValid = this.validation[name]?.required

                handleErrorElement(errorElement, true, isValid)

                if (!isValid) {
                    disableSubmitButton = true
                }

                continue
            }

            for (let validationTypeName in validationType) {
                const isValid = this.validation[name][validationTypeName]

                const { errorElement, touched, field } = this.fields[name]

                if (!isValid) {
                    disableSubmitButton = true
                }

                if (this.config && this.config.onValidate) {
                    this.config.onValidate(name, isValid, validationTypeName)
                }

                if (
                    touched &&
                    !isValid &&
                    (field instanceof HTMLInputElement ||
                        field instanceof HTMLTextAreaElement) &&
                    this.config &&
                    this.config.invalidClasses &&
                    field.classList &&
                    field.classList.length
                ) {
                    field.classList.add(
                        ...this.config.invalidClasses.split(' ')
                    )
                } else if (
                    touched &&
                    isValid &&
                    (field instanceof HTMLInputElement ||
                        field instanceof HTMLTextAreaElement) &&
                    this.config &&
                    this.config.invalidClasses &&
                    field.classList &&
                    field.classList.length
                ) {
                    field.classList.remove(
                        ...this.config.invalidClasses.split(' ')
                    )
                }

                handleErrorElement(errorElement, touched, isValid)
            }
        }

        if (disableSubmitButton && this.config && this.btnSubmit) {
            const submitButton = this.btnSubmit

            if (submitButton instanceof HTMLButtonElement) {
                submitButton.setAttribute('disabled', 'disabled')
            } else {
                submitButton.style.pointerEvents = 'none'
                submitButton.style.cursor = 'default'
            }
        } else if (this.config && this.config.submitButton && this.btnSubmit) {
            const submitButton = this.btnSubmit

            if (submitButton instanceof HTMLButtonElement) {
                submitButton.removeAttribute('disabled')
            } else {
                submitButton.style.pointerEvents = ''
                submitButton.style.cursor = ''
            }
        }
    }

    private validateAll() {
        for (let name in this.fields) {
            this.validate(name)
        }
        for (let name in this.radios) {
            this.validate(name)
        }
    }

    get isValid(): boolean {
        for (let name in this.fields) {
            for (let validationName in this.validation[name]) {
                const isValid = this.validation[name][validationName]
                if (!isValid) {
                    this.validate(name)
                    return false
                }
            }
        }

        return true
    }

    setValueFromObject(objectName: string, object: any) {
        if (!object || Array.isArray(object) || typeof object !== 'object') {
            throw new Error(
                '(setValueFromObject) argument must be an key/value pair object'
            )
        }

        for (const key in object) {
            const value = object[key]
            this.setValue(`${objectName}.${key}`, value)
        }
    }

    deleteValue(name: string) {
        delete this.formState.state[name]
        this.formState.setState({ ...this.formState.state })
    }

    setPrivateValue(name: string, value: any) {
        this.privateValues[name] = value
        this.setValue(name, value)
    }

    private silence: boolean = false

    setSilence(value: boolean) {
        this.silence = value
    }

    setValue(name: string, value: any, origin?: string) {
        if (
            value === undefined ||
            value === null ||
            value === 'null' ||
            value === 'undefined'
        ) {
            return
        }

        const isInRadio = name in this.radios

        let field: Field

        if (name in this.radios) {
            field = this.radios[name][`${value}`]?.field
        } else {
            field = this.fields[name]?.field
        }

        if (!field) {
            delete this.validation[name]
        }

        const setTheValue = () => {
            if (name.includes('.')) {
                const split = name.split('.')
                const parent = split[0]
                const child = split[1]

                if (!this.formState.state[parent]) {
                    this.formState.state[parent] = {}
                }

                if (field && field instanceof HTMLInputElement) {
                    if (field.type == 'checkbox') {
                        this.formState.state[parent][child] = Boolean(value)
                        field.checked = Boolean(value)
                    } else if (field.type == 'radio') {
                        this.formState.state[parent][child] = value
                        field.checked = true
                    } else {
                        this.formState.state[parent][child] = value
                        field.value = value
                    }
                } else {
                    this.formState.state[parent][child] = value
                    if (field) {
                        field.value = value
                    }
                }
            } else if (field && field instanceof HTMLInputElement) {
                if (field.type == 'checkbox') {
                    this.formState.state[name] = Boolean(value)
                    field.checked = Boolean(value)
                } else if (field.type == 'radio') {
                    this.formState.state[name] = value
                    field.checked = true
                } else {
                    this.formState.state[name] = `${value}`
                    field.value = `${value}`
                }
            } else {
                this.formState.state[name] = value

                if (field) {
                    field.value = value
                }
            }

            if (field && !origin && !isInRadio) {
                this.checkData(name)
            }

            this.validate(name)

            this.formState.setState({ ...this.formState.state })

            if (this.onChange && !this.privateValues[name] && !this.silence) {
                this.onChange(name, value)
            }
        }

        setTheValue()
    }

    private checkData(name: string) {
        this.transform(name)
        this.remove(name)
        this.mask(name)
        this.currency(name)
    }

    private root: Element | null = null

    private formRef: React.RefObject<HTMLElement> | null = null

    private fieldsChanged: number = 0

    private destroyed: boolean = false

    private attemptsToFindRoot: number = 0

    get attempts() {
        return this.attemptsToFindRoot
    }

    get rootElement() {
        return this.root
    }

    async init(
        formRef: React.RefObject<HTMLElement>,
        state: [Mixed, React.Dispatch<React.SetStateAction<Mixed>>],
        submitButtonRef?: React.RefObject<HTMLButtonElement>,
        config?: Config
    ) {
        config = {
            submitButton: submitButtonRef,
        }

        this.attemptsToFindRoot = 0
        this.destroyed = false
        await this.initialize(formRef, state, config)
    }

    private async initialize(
        formRef: React.RefObject<HTMLElement>,
        state: [Mixed, React.Dispatch<React.SetStateAction<Mixed>>],
        config?: Config,
        byMySelf: boolean = false
    ) {
        if (this.destroyed) {
            return
        }

        this.fields = {}
        this.validation = {}
        this.radios = {}
        this.formState.state = state[0]
        this.formState.setState = state[1]

        document.body.onclick = click(this)

        if (!formRef) {
            return
        }

        if (document.readyState !== 'complete') {
            await new Promise(resolve => setTimeout(resolve, 1000))
            this.initialize(formRef, state, config, true)
            return
        }

        this.formRef = formRef
        this.root = formRef.current

        if (!this.root) {
            if (this.attemptsToFindRoot >= 20) {
                return
            }

            if (this.destroyed && byMySelf) {
                return
            } else {
                this.destroyed = false
            }
            await new Promise(resolve => setTimeout(resolve, 1000))
            this.initialize(formRef, state, config, true)

            ++this.attemptsToFindRoot
            return
        } else {
            this.attemptsToFindRoot = 20
        }

        this._config = config

        const inputFields = this.root.getElementsByTagName('input')
        const selectFields = this.root.getElementsByTagName('select')
        const textareaFields = this.root.getElementsByTagName('textarea')

        for (let x = 0; x < inputFields.length; x++) {
            const field = inputFields[x]

            if (!field.name && !field.dataset.ignore) {
                continue
            }

            this.addField({
                type: 'input',
                field,
            })
        }

        for (let x = 0; x < selectFields.length; x++) {
            const select = selectFields[x]

            if (!select.name && !select.dataset.ignore) {
                continue
            }

            this.addField({
                type: 'select',
                field: select,
            })
        }

        for (let x = 0; x < textareaFields.length; x++) {
            const textarea = textareaFields[x]

            if (!textarea.name && !textarea.dataset.ignore) {
                continue
            }

            this.addField({
                type: 'textarea',
                field: textarea,
            })
        }

        this.addEvents()
        this.validateAll()

        let mutationsQueue: MutationRecord[][] = []
        let observer: MutationObserver

        const searchRemovedFields = (node: Node) => {
            if (
                node instanceof HTMLInputElement ||
                node instanceof HTMLTextAreaElement ||
                node instanceof HTMLSelectElement
            ) {
                ++this.fieldsChanged
            } else {
                for (let domNode of node.childNodes) {
                    searchRemovedFields(domNode)
                }
            }
        }

        const checkAddedFields = (node: Node) => {
            if (
                node instanceof HTMLInputElement ||
                node instanceof HTMLTextAreaElement ||
                node instanceof HTMLSelectElement
            ) {
                ++this.fieldsChanged
            } else {
                for (let domNode of node.childNodes) {
                    searchRemovedFields(domNode)
                }
            }
        }

        const processQueue = async () => {
            if (!mutationsQueue.length) {
                return
            }

            const currentQueue = mutationsQueue[0]

            for (let mutation of currentQueue) {
                for (let node of mutation.removedNodes) {
                    searchRemovedFields(node)
                }

                if (mutation.addedNodes.length) {
                    for (let node of mutation.addedNodes) {
                        checkAddedFields(node)
                    }
                }
            }

            mutationsQueue.shift()

            if (this.fieldsChanged && !mutationsQueue.length) {
                this.fieldsChanged = 0
                observer.disconnect()
                this.reinitialize()
            }

            if (mutationsQueue.length) {
                processQueue()
            }
        }

        observer = new MutationObserver((mutations, observer) => {
            if (!mutationsQueue.length) {
                mutationsQueue.push(mutations)
                processQueue()
            } else {
                mutationsQueue.push(mutations)
            }
        })

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        })

        const readyEvents = this.events.ready

        if (readyEvents && readyEvents.length) {
            for (let ready of readyEvents) {
                ready.action.apply(null, ready.deps)
            }
        }
    }

    destroy() {
        for (const name in this.fields) {
            const { field } = this.fields[name]

            if (field) {
                if (field && field instanceof HTMLInputElement) {
                    if (field.type == 'checkbox') {
                        field.checked = false
                    } else if (field.type == 'radio') {
                        field.value = ''
                        field.checked = false
                    } else {
                        field.value = ''
                        field.oninput = null
                        field.onkeyup = null
                        field.onfocus = null
                        field.onblur = null
                    }
                } else {
                    field.value = ''
                }
            }
        }

        for (const name in this.radios) {
            const someKey = Object.keys(this.radios[name])[0]

            const { field } = this.radios[name][someKey]

            if (field && field instanceof HTMLInputElement) {
                field.value = ''
                field.checked = false
            }
        }

        if (this._config?.initialState) {
            this._config.initialState = undefined
        }

        this.formState.state = {}
        this.formState.setState = () => {}
        this._config = {}
        this.events = {
            ready: [],
            init: [],
        }
        this.fields = {}
        this.radios = {}
        this.validation = {}
        this.formRef = null
        this.root = null
        this.destroyed = true
        document.body.onclick = null
    }

    on(
        type: StateEvents,
        name: string,
        action: (...args: any[]) => any,
        deps: any[] = []
    ) {
        if (!this.events[type]) {
            this.events[type] = []
        }

        const index = this.events[type].findIndex(action => action.name == name)

        if (index === -1) {
            this.events[type].push({
                name,
                action,
                deps,
            })
        } else {
            this.events[type][index] = {
                name,
                action,
                deps,
            }
        }
    }

    ready(name: string, action: (...args: any[]) => any, deps: any[] = []) {
        this.on('ready', name, action, deps)
    }

    async reinitialize() {
        this.attemptsToFindRoot = 0
        await this.initialize(
            this.formRef as React.RefObject<HTMLElement>,
            [this.formState.state, this.formState.setState],
            this._config
        )
    }

    get rootNode() {
        return this.root
    }

    disableSubmitButton() {
        if (this.btnSubmit) {
            const submitButton = this.btnSubmit
            if (submitButton instanceof HTMLButtonElement) {
                submitButton.setAttribute('disabled', 'disabled')
            } else {
                submitButton.style.pointerEvents = 'none'
                submitButton.style.cursor = 'default'
            }
        }
    }

    enableSubmitButton() {
        if (this.btnSubmit) {
            const submitButton = this.btnSubmit

            if (submitButton instanceof HTMLButtonElement) {
                submitButton.removeAttribute('disabled')
            } else {
                submitButton.style.pointerEvents = ''
                submitButton.style.cursor = ''
            }
        }
    }
}

function click(state: ReactFormState) {
    return () => {
        if (state.rootElement || document.body.contains(state.rootElement)) {
            return
        }

        // finding
        if (state.attempts < 20) {
            return
        }

        state.reinitialize()
    }
}

export function useFormState(id: string): {
    formRef: React.RefObject<any>
    submitButtonRef: React.RefObject<HTMLButtonElement>
} {
    const [formState, setState] = useState<Mixed>({})

    if (!ReactFormState.getInstance(id)) {
        ReactFormState.setup(id)
    }

    const formRef = useRef<HTMLElement>(null as any)
    const submitButtonRef = useRef<HTMLButtonElement>(null as any)

    useEffect(() => {
        const state = ReactFormState.getInstance(id)
        state.init(formRef, [formState, setState], submitButtonRef)
        return () => {
            state.destroy()
        }
    }, [])

    return { formRef, submitButtonRef }
}
